import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { StockActions } from '@toro/common/shared/state-management/stock/stock.action';
import { TrackingsService } from '../../../../../services/tracking.service';
import { RequestSinacorNoteAction } from '../../../../../state-management/report-sinacor/report-sinacor.action';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { filter, tap } from 'rxjs/operators';
import { SuccessDialogComponent } from './dialogs/success-dialog/success-dialog.component';
import { AlertDialogComponent } from './dialogs/alert-dialog/alert-dialog.component';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'sinacor-note',
    templateUrl: './sinacor-note.component.html',
    styleUrls: ['./sinacor-note.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SinacorNoteComponent extends DialogPanelComponentContent implements OnDestroy {
    public startDate: Date;
    public finalDate: Date;
    public formDatePicker: FormGroup;
    public isMobile: boolean;
    public brokerageNotesList: string[] = [];
    public pending: boolean = false;
    private subscriptions: Subscription = new Subscription();

    public constructor(
        public formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        @Inject(DOCUMENT) private document: Document,
        public elementRef: ElementRef,
        private dialogReference: MatDialogRef<SinacorNoteComponent>,
        private router: Router,
        private tracking: TrackingsService,
        private utilitiesService: UtilitiesService,
        private store: Store<any>,
        private dialogsService: DialogsService
    ) {
        super(elementRef, true);
        this.document.body.classList.add('freeze');
        this.isMobile = this.utilitiesService.isMobile();

        this.formDatePicker = this.formBuilder.group({
            formDatePickerStartField: ['', Validators.required],
            formDatePickerFinalField: ['', Validators.required],
            stock: ['', Validators.required],
            future: ['', Validators.required],
        });

        this.initListeners();
        this.store.dispatch({ type: StockActions.getNoteListDate });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.document.body.classList.remove('freeze');
    }

    public initListeners = () => {
        this.initFormListener();
        this.initBrokerageNotesListener();
        this.initSinacorNoteDownloadListener();
    }

    public initFormListener = () => {
        this.subscriptions.add(this.formDatePicker.controls['formDatePickerStartField'].valueChanges.pipe(
            tap(value => {
                if (!this.formDatePicker.controls['formDatePickerFinalField'].value) {
                    this.formDatePicker.controls['formDatePickerFinalField'].setValue(value);
                }
                this.checkAvailableDateRange(this.formDatePicker.controls['formDatePickerStartField']);
            })
        ).subscribe());

        this.subscriptions.add(this.formDatePicker.controls['formDatePickerFinalField'].valueChanges.pipe(
            tap(() => {
                this.checkAvailableDateRange(this.formDatePicker.controls['formDatePickerFinalField']);
            })
        ).subscribe());
    }

    public checkAvailableDateRange(formDatePicker: AbstractControl) {
        if (formDatePicker.value && this.getFormatedDate(formDatePicker.value).split('/')[1] === this.getFormatedDate(new Date()).split('/')[1]) {
            formDatePicker.setValue('');
            this.showAlertDateRange();
        }
    }

    public showAlertDateRange() {
        if (!this.formDatePicker.controls['formDatePickerStartField'].value && !this.formDatePicker.controls['formDatePickerFinalField'].value) {
            this.dialogsService.showComponentAsAsidePanel(AlertDialogComponent, true);
        }
    }

    public initBrokerageNotesListener = () => {
        this.subscriptions.add(this.store.select((brokerageNotesStore) => brokerageNotesStore.stocks.incomeDates).pipe(
            filter((incomeDates) => !!incomeDates && incomeDates.length > 0)
        ).subscribe((stockDates) => {
            this.brokerageNotesList = stockDates.map(stockDate => this.getFormatedDate(stockDate.referenceDate));
        }));
    }

    public initSinacorNoteDownloadListener = () => {
        this.subscriptions.add(this.store.select((sinacorNotesStore) => sinacorNotesStore.requestSinacorNote.requestedNote).pipe(
            filter((requestedNote) => !!requestedNote)
        ).subscribe((requestedNote) => {
            this.tracking.sendMetricsToMixpanel(
                ActionEventsEnum.USER_CLICKED,
                {
                    'element': '[Stocks] Sinacor Request',
                    'origin': 'Nota Sinacor',
                    'Ações (Bovespa)': !!this.formDatePicker.controls['stock'].value,
                    'BMF (Futuros)': !!this.formDatePicker.controls['future'].value,
                }
            );
            this.pending = false;
            this.closeDialog();
            this.dialogsService.showComponentAsAsidePanel(SuccessDialogComponent, true);
        }));
    }

    public closeDialog() {
        this.document.body.classList.remove('freeze');
        this.dialogReference.close();
        this.router.navigateByUrl('/meus-investimentos');
    }

    public isValidConfirmButton() {
        return (
            !!this.formDatePicker.controls['formDatePickerStartField'].value &&
            !!this.formDatePicker.controls['formDatePickerFinalField'].value &&
            (this.formDatePicker.controls['stock'].value === true ||
                this.formDatePicker.controls['future'].value === true)
        );
    }

    public filterDatePicker = (date: Date): boolean => {
        const today = this.getFormatedDate(new Date());
        const currentDate = this.getFormatedDate(date);

        if (currentDate === today) {
            return false;
        }

        return this.brokerageNotesList.includes(currentDate);
    }

    public downloadSinacorNote() {
        const dateFrom = this.getFormatedDate(this.formDatePicker.controls['formDatePickerStartField'].value);
        const dateTo = this.getFormatedDate(this.formDatePicker.controls['formDatePickerFinalField'].value);
        const bovespaCheckbox = !!this.formDatePicker.controls['stock'].value;
        const bmfCheckbox = !!this.formDatePicker.controls['future'].value;

        const payload = {
            "markets": {
                "stock": bovespaCheckbox,
                "future": bmfCheckbox
            },
            "dateFrom": dateFrom,
            "dateTo": dateTo
        }
        this.pending = true;
        this.store.dispatch({ type: RequestSinacorNoteAction.requestSinacorNote, payload: { ...payload } });
    }

    public getFormatedDate(date: Date) {
        return new Date(date).toISOString().split('T')[0].split('-').join('/');
    }
}
